/**
 * People collection — typed CRUD on top of the generic store.
 *
 * Wrap the raw store calls so views deal in `Person` and not `unknown`,
 * and so we can centralise things like timestamp stamping and seed
 * bootstrap. When we move to Firestore, the only thing this file
 * changes is the import in store.ts.
 */

import { db, nowIso, uuid } from './store';
import type { Person, PersonActivity, PersonCategory } from './types';
import { demoPeople, seedPeople } from './seed';
import { useFirestore } from '../firebase';

export async function listPeople(): Promise<Person[]> {
  return db.list('people');
}

export async function listByCategory(category: PersonCategory): Promise<Person[]> {
  const all = await db.list('people');
  return all.filter((p) => p.category === category);
}

export async function getPerson(id: string): Promise<Person | null> {
  return db.get('people', id);
}

export type NewPersonInput = Omit<Person, 'id' | 'createdAt' | 'updatedAt' | 'tags'> & {
  tags?: string[];
};

export async function createPerson(input: NewPersonInput): Promise<Person> {
  const ts = nowIso();
  const record: Person = {
    ...input,
    tags: input.tags ?? [],
    id: uuid(),
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('people', record);
  return record;
}

export async function updatePerson(
  id: string,
  patch: Partial<Omit<Person, 'id' | 'createdAt'>>,
): Promise<Person> {
  return db.update('people', id, { ...patch, updatedAt: nowIso() });
}

export async function deletePerson(id: string): Promise<void> {
  await db.remove('people', id);
}

/**
 * Append an entry to a person's activity timeline and re-stamp
 * `updatedAt`. This is the canonical "log a touch" call — used by the
 * detail view's quick-log composer and, later, by the chat agent
 * (which logs `system`-kind entries when it acts on a contact).
 */
export async function logPersonActivity(
  id: string,
  kind: PersonActivity['kind'],
  text: string,
  actor = 'You',
): Promise<Person> {
  const existing = await db.get('people', id);
  if (!existing) throw new Error(`No person ${id}`);
  const entry: PersonActivity = { id: uuid(), at: nowIso(), kind, actor, text };
  return db.update('people', id, {
    activity: [...(existing.activity ?? []), entry],
    updatedAt: nowIso(),
  });
}

export function subscribePeople(fn: (people: Person[]) => void): () => void {
  return db.subscribe('people', fn);
}

export async function bootstrapPeopleIfEmpty(): Promise<void> {
  const existing = await db.list('people');
  if (existing.length === 0) {
    // Live (Firestore) runs start from the empty `seedPeople` so production
    // is never polluted with mock contacts; local/mock runs get the rich
    // `demoPeople` roster so the CRM, scoring, reminders and chat answerer
    // have realistic data to work against.
    await db.replaceAll('people', useFirestore ? seedPeople : demoPeople);
  }
}
